import axios from 'axios';
import { API_CONFIG, STORAGE_KEYS } from '../../constants';
import {
  ICampaign,
  CampaignFilters,
  CampaignPagination,
  CampaignReview,
  CampaignAnalytics,
  CampaignContactCounts,
} from '../../types/campaigns/campaigns.types';

const SERVER_URL = API_CONFIG.SERVER_URL;

// Create axios instance with default config
const api = axios.create({
  baseURL: SERVER_URL,
});

// Add request interceptor to include token in headers
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid, redirect to login
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Get all campaigns with optional filters and pagination
export const getCampaigns = async (
  filters?: CampaignFilters
): Promise<{ campaigns: ICampaign[]; pagination: CampaignPagination }> => {
  try {
    const params = new URLSearchParams();

    if (filters?.page !== undefined)
      params.append('page', filters.page.toString());
    if (filters?.limit !== undefined)
      params.append('limit', filters.limit.toString());
    if (filters?.search) params.append('search', filters.search);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);

    const response = await api.get(`/campaigns?${params}`);
    return {
      campaigns: response.data.campaigns || response.data.data || [],
      pagination: response.data.pagination || {
        page: filters?.page || 0,
        limit: filters?.limit || 10,
        total: response.data.total || 0,
        totalPages: response.data.totalPages || 0,
      },
    };
  } catch (error) {
    console.error('Error fetching campaigns:', error);
    throw error;
  }
};

// Get campaign by ID
export const getCampaign = async (campaignId: string): Promise<ICampaign> => {
  try {
    const response = await api.get(`/campaigns/${campaignId}`);
    return response.data.campaign;
  } catch (error) {
    console.error('Error fetching campaign:', error);
    throw error;
  }
};

// Create new campaign
export const createCampaign = async (campaignData: {
  name: string;
}): Promise<ICampaign> => {
  try {
    const response = await api.post('/campaigns', campaignData);
    // Handle different possible response structures
    return response.data.campaign || response.data || response;
  } catch (error) {
    console.error('Error creating campaign:', error);
    throw error;
  }
};

// Update campaign
export const updateCampaign = async (
  campaignId: string,
  campaignData: {
    name?: string;
    description?: string;
    goal?: any;
    exitSurvey?: string;
  }
): Promise<ICampaign> => {
  try {
    const response = await api.patch(`/campaigns/${campaignId}`, campaignData);
    // Handle different possible response structures
    return response.data.campaign || response.data || response;
  } catch (error) {
    console.error('Error updating campaign:', error);
    throw error;
  }
};

// Delete campaign
export const deleteCampaign = async (campaignId: string): Promise<void> => {
  try {
    await api.delete(`/campaigns/${campaignId}`);
  } catch (error) {
    console.error('Error deleting campaign:', error);
    throw error;
  }
};

// Pause campaign
export const pauseCampaign = async (campaignId: string): Promise<ICampaign> => {
  try {
    const response = await api.patch(`/campaigns/${campaignId}/pause`);
    return response.data.campaign || response.data;
  } catch (error) {
    console.error('Error pausing campaign:', error);
    throw error;
  }
};

// Publish campaign
export const publishCampaign = async (
  campaignId: string,
  type?: 'all_contacts' | 'future_contacts'
): Promise<ICampaign> => {
  try {
    const payload = type ? { type } : {};
    const response = await api.put(`/campaigns/${campaignId}/publish`, payload);
    return response.data.campaign || response.data;
  } catch (error) {
    console.error('Error publishing campaign:', error);
    throw error;
  }
};

// Review campaign
export const reviewCampaign = async (
  campaignId: string
): Promise<CampaignReview> => {
  try {
    const response = await api.get(`/campaigns/${campaignId}/review`);
    return response.data;
  } catch (error) {
    console.error('Error reviewing campaign:', error);
    throw error;
  }
};

// Get campaign analytics
export const getCampaignAnalytics = async (
  campaignId: string,
  startDate?: string,
  endDate?: string
): Promise<CampaignAnalytics> => {
  try {
    const params = new URLSearchParams();
    if (startDate) {
      params.append('startDate', startDate);
    }
    if (endDate) {
      params.append('endDate', endDate);
    }

    const response = await api.get(
      `/campaigns/${campaignId}/analytics?${params}`
    );
    return response.data.data;
  } catch (error) {
    console.error('Error fetching campaign analytics:', error);
    throw error;
  }
};

// Duplicate campaign
export const duplicateCampaign = async (
  campaignId: string
): Promise<ICampaign> => {
  try {
    const response = await api.post(`/campaigns/${campaignId}/duplicate`);
    return response.data.campaign || response.data;
  } catch (error) {
    console.error('Error duplicating campaign:', error);
    throw error;
  }
};

// Get campaign contact counts
export const getCampaignContactCounts = async (
  campaignId: string
): Promise<CampaignContactCounts> => {
  try {
    const response = await api.get(`/campaigns/${campaignId}/contacts-count`);
    return response.data.data;
  } catch (error) {
    console.error('Error fetching campaign contact counts:', error);
    throw error;
  }
};

export default api;
